<script>
import { mapGetters } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import AccountAPI from 'dashboard/api/account';
import UpgradeHighlight from './components/UpgradeHighlight.vue';
import UpgradePlanCard from './components/UpgradePlanCard.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

export default {
  components: {
    UpgradeHighlight,
    UpgradePlanCard,
    BaseSettingsHeader,
  },
  setup() {
    const { accountId } = useAccount();
    return {
      accountId,
    };
  },
  data() {
    return {
      plans: [],
      currentPlanId: null,
      isFetchingPlans: false,
      fetchError: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
    }),
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
    locale() {
      return this.$i18n?.locale || navigator.language || 'en-US';
    },
    overallLoading() {
      return this.uiFlags.isFetchingItem || this.isFetchingPlans;
    },
    pageDescription() {
      return this.$t('UPGRADE_SETTINGS.DESCRIPTION');
    },
    highlightCopy() {
      return this.$t('UPGRADE_SETTINGS.HIGHLIGHT');
    },
    supportNote() {
      return this.$t('UPGRADE_SETTINGS.SUPPORT_NOTE');
    },
    planCards() {
      return (this.plans || []).map(plan => this.decoratePlan(plan));
    },
  },
  mounted() {
    this.fetchPlans();
  },
  methods: {
    async fetchPlans() {
      this.isFetchingPlans = true;
      this.fetchError = null;
      try {
        const response = await AccountAPI.getBillingPlans();
        const payload = response?.data || {};
        this.plans = payload.plans || [];
        this.currentPlanId = payload.current_plan_id || null;
      } catch (error) {
        this.fetchError =
          error?.response?.data?.error ||
          error?.message ||
          this.$t('UPGRADE_SETTINGS.ERROR.FETCH_FAILED');
      } finally {
        this.isFetchingPlans = false;
      }
    },
    decoratePlan(plan) {
      const isCurrent = this.currentPlanId && plan.id === this.currentPlanId;
      const pricing = this.preparePricing(plan);
      const limits = this.prepareLimits(plan.max_limits || {});
      const freePlan = this.isFreePlan(plan, pricing);
      if (freePlan) {
        pricing.free = true;
      }
      const requiresContact = !pricing.hasPrice && !freePlan;
      const action = this.resolvePlanAction({ isCurrent, requiresContact, freePlan });
      const badge = this.resolveBadge(plan, isCurrent);

      return {
        ...plan,
        isCurrent,
        pricing,
        limits,
        requiresContact,
        freePlan,
        action,
        badge,
        isFeatured: badge?.variant === 'featured',
      };
    },
    prepareLimits(limits) {
      const labelMap = {
        seats: 'SEATS',
        inboxes: 'INBOXES',
        faqs: 'FAQS',
        data_items: 'DATA_ITEMS',
        images: 'IMAGES',
        data_collection_fields: 'DATA_COLLECTION_FIELDS',
        ai_rules: 'AI_RULES',
        ai_sample_sentences: 'AI_SAMPLES',
        ai_replies_per_month: 'AI_REPLIES',
        ai_replies_per_month_byo: 'AI_REPLIES_BYO',
        web_sources: 'WEB_SOURCES',
      };

      return Object.entries(labelMap)
        .map(([key, translationKey]) => {
          const rawValue = limits[key];
          const hasValue = rawValue !== null && rawValue !== undefined;
          const displayValue = hasValue
            ? this.formatNumber(rawValue)
            : this.$t('USAGE_SETTINGS.UNLIMITED');

          return {
            key,
            label: this.$t(`USAGE_SETTINGS.PLAN_LIMITS.LABELS.${translationKey}`),
            value: displayValue,
            visible: hasValue || key === 'seats' || key === 'inboxes',
          };
        })
        .filter(item => item.visible);
    },
    preparePricing(plan) {
      const priceByInterval = interval =>
        (plan.prices || []).find(price => price.interval === interval);

      const yearlyRaw = priceByInterval('yearly');
      const monthlyRaw = priceByInterval('monthly');

      const yearly = yearlyRaw ? this.describePrice(yearlyRaw) : null;
      const monthly = monthlyRaw ? this.describePrice(monthlyRaw) : null;

      let note = null;
      const discount = yearlyRaw?.yearly_discount_percent;
      if (discount) {
        note = this.$t('UPGRADE_SETTINGS.PRICING.YEARLY_SAVE', { percent: discount });
      } else if (yearlyRaw?.intro_discount_percent) {
        note = this.$t('UPGRADE_SETTINGS.PRICING.YEARLY_SAVE', {
          percent: yearlyRaw.intro_discount_percent,
        });
      }

      return {
        yearly,
        monthly,
        note,
        hasPrice: Boolean(yearly || monthly),
      };
    },
    describePrice(price) {
      const amount = Number(price.unit_amount_cents) / 100;
      if (!Number.isFinite(amount)) {
        return null;
      }

      try {
        const formatter = new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: price.currency,
          maximumFractionDigits: 2,
        });
        const formatted = formatter.format(amount);
        const parts = formatter.formatToParts(amount);
        const symbol = parts.find(part => part.type === 'currency')?.value || '';
        const numeric = parts
          .filter(part => ['integer', 'group', 'decimal', 'fraction'].includes(part.type))
          .map(part => part.value)
          .join('');

        const suffix = price.billing_model === 'per_seat'
          ? this.$t('UPGRADE_SETTINGS.PRICING.PER_SEAT')
          : '';

        return {
          display: formatted,
          symbol,
          amount: numeric,
          suffix,
          interval: price.interval,
          label: this.billingIntervalLabel(price.interval),
        };
      } catch (error) {
        return {
          display: this.formatPrice(price.unit_amount_cents, price.currency),
          symbol: '',
          amount: '',
          suffix: '',
          interval: price.interval,
          label: this.billingIntervalLabel(price.interval),
        };
      }
    },
    resolvePlanAction({ isCurrent, requiresContact, freePlan }) {
      if (isCurrent) {
        return {
          type: 'current',
          label: this.$t('UPGRADE_SETTINGS.ACTION.CURRENT_PLAN'),
          icon: 'check-check',
          disabled: true,
        };
      }

      if (requiresContact) {
        return {
          type: 'contact',
          label: this.$t('UPGRADE_SETTINGS.ACTION.CONTACT_SUPPORT'),
          icon: 'chat-multiple',
          disabled: false,
        };
      }

      return {
        type: 'portal',
        label: freePlan
          ? this.$t('UPGRADE_SETTINGS.ACTION.SELECT_FREE_PLAN')
          : this.$t('UPGRADE_SETTINGS.ACTION.SELECT_PLAN'),
        icon: 'credit-card',
        disabled: false,
      };
    },
    isFreePlan(plan, pricing) {
      const priceList = plan.prices || [];
      const hasZeroPrice = priceList.some(price => Number(price.unit_amount_cents) === 0);
      if (hasZeroPrice) {
        return true;
      }
      if (!pricing.hasPrice && priceList.length === 0) {
        return Boolean(plan.is_default);
      }
      return false;
    },
    resolveBadge(plan, isCurrent) {
      if (isCurrent) {
        return {
          label: this.$t('UPGRADE_SETTINGS.BADGE.CURRENT'),
          variant: 'current',
          className: 'upgrade-card__badge--current',
        };
      }

      if (plan.is_default) {
        return {
          label: this.$t('UPGRADE_SETTINGS.BADGE.POPULAR'),
          variant: 'featured',
          className: 'upgrade-card__badge--featured',
        };
      }

      if (plan.support_tier === 'premium') {
        return {
          label: this.$t('UPGRADE_SETTINGS.BADGE.PREMIUM'),
          variant: 'premium',
          className: 'upgrade-card__badge--premium',
        };
      }

      return null;
    },
    formatPrice(amountCents, currency) {
      if (amountCents === null || amountCents === undefined) {
        return this.$t('UPGRADE_SETTINGS.PRICING.CONTACT');
      }

      const amount = Number(amountCents) / 100;
      if (!Number.isFinite(amount)) {
        return this.$t('UPGRADE_SETTINGS.PRICING.CONTACT');
      }

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)} ${currency}`;
      }
    },
    formatNumber(value) {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        return numeric.toLocaleString(this.locale);
      }
      return value;
    },
    handlePlanAction(plan) {
      if (!plan?.action || plan.action.disabled) {
        return;
      }

      if (plan.action.type === 'contact') {
        this.onToggleChatWindow();
        return;
      }

      this.onClickBillingPortal();
    },
    billingIntervalLabel(interval = '') {
      const normalized = interval.toUpperCase();
      const key = `BILLING_SETTINGS.BILLING_CYCLE_LABELS.${normalized}`;
      const label = this.$t(key);
      return label === key ? interval : label;
    },
    onClickBillingPortal() {
      this.$store.dispatch('accounts/checkout');
    },
    onToggleChatWindow() {
      if (window.$hichat) {
        window.$hichat.toggle();
      }
    },
    goToBilling() {
      this.$router.push({
        name: 'billing_settings_index',
        params: { accountId: this.accountId },
      });
    },
    goToUsage() {
      this.$router.push({
        name: 'settings_usage_index',
        params: { accountId: this.accountId },
      });
    },
  },
};
</script>

<template>
  <div>
    <woot-loading-state v-if="overallLoading" />
    <div v-else class="space-y-6">
      <BaseSettingsHeader
       :description="$t('UPGRADE_SETTINGS.DESCRIPTION')"
        feature-name="upgrade"
      />

      <UpgradeHighlight
        :page-description="pageDescription"
        :highlight-copy="highlightCopy"
        :support-note="supportNote"
        @go-billing="goToBilling"
        @go-usage="goToUsage"
      />

      <section v-if="fetchError" class="upgrade-alert">
        <lucide-icon name="triangle-alert" :size="18" />
        <span>{{ fetchError }}</span>
      </section>

      <section v-else class="upgrade-grid">
        <UpgradePlanCard
          v-for="plan in planCards"
          :key="plan.id"
          :plan="plan"
          class="w-full h-full"
          @action="handlePlanAction"
        />
      </section>
    </div>
  </div>
</template>

<style lang="scss">
.upgrade-alert {
  @apply flex items-center gap-2 rounded-3xl border border-red-200/70 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200;
}

.upgrade-grid {
  @apply grid gap-4 md:gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 justify-items-center;
}
</style>
